import {
  UseMutationResult,
  useMutation,
  UseQueryOptions,
  UseQueryResult,
  useQuery,
} from '@tanstack/react-query';

import { ProfileEnum } from '@models/clinic-list';
import type {
  BookingReservePayload,
  GetCalendarManagementPayload,
  GetCalendarManagementResponse,
  GetClinicPlaceDoctorsResponse,
  GetClinicProfileResponse,
  GetUserInsurancesByProfilePayload,
  GetUserInsurancesByProfileResponse,
  GetUserServicesByProfilePayload,
  GetUserServicesByProfileResponse,
  FilterDoctorsBySpecialityAndPlacePayload,
  FilterDoctorsBySpecialityAndPlaceResponse,
  SearchClinicPayload,
  SearchClinicResponse,
} from '@models/clinic-list';

import {
  reserveBooking,
  getCalendarManagement,
  getClinicProfile,
  getClinicPlaceDoctors,
  getUserServicesByProfile,
  getUserInsurancesByProfile,
  filterDoctorsBySpecialityAndPlace,
  searchClinic,
} from './index';

export const useGetClinicProfile = (
  profileOwnerId: number,
  profileType: ProfileEnum,
  options?: Omit<UseQueryOptions<GetClinicProfileResponse, Error>, 'queryKey'>,
): UseQueryResult<GetClinicProfileResponse, Error> => {
  return useQuery<GetClinicProfileResponse, Error>({
    queryKey: ['getClinicProfile', profileOwnerId, profileType],
    queryFn: () => getClinicProfile(profileOwnerId, profileType),
    enabled: !!profileOwnerId,
    ...options,
  });
};

export const useGetClinicPlaceDoctors = (
  profileId: number,
  options?: Omit<
    UseQueryOptions<GetClinicPlaceDoctorsResponse, Error>,
    'queryKey'
  >,
): UseQueryResult<GetClinicPlaceDoctorsResponse, Error> => {
  return useQuery<GetClinicPlaceDoctorsResponse, Error>({
    queryKey: ['getClinicPlaceDoctors', profileId],
    queryFn: () => getClinicPlaceDoctors(profileId),
    enabled: !!profileId,
    ...options,
  });
};

export const useGetUserInsurancesByProfile = (
  payload: GetUserInsurancesByProfilePayload,
  options?: Omit<
    UseQueryOptions<GetUserInsurancesByProfileResponse, Error>,
    'queryKey'
  >,
): UseQueryResult<GetUserInsurancesByProfileResponse, Error> => {
  return useQuery<GetUserInsurancesByProfileResponse, Error>({
    queryKey: ['getUserInsurancesByProfile', payload],
    queryFn: () => getUserInsurancesByProfile(payload),
    enabled: !!payload.profileId,
    ...options,
  });
};

export const useGetUserServicesByProfile = (
  payload: GetUserServicesByProfilePayload,
  options?: Omit<
    UseQueryOptions<GetUserServicesByProfileResponse, Error>,
    'queryKey'
  >,
): UseQueryResult<GetUserServicesByProfileResponse, Error> => {
  return useQuery<GetUserServicesByProfileResponse, Error>({
    queryKey: ['getUserServicesByProfile', payload],
    queryFn: () => getUserServicesByProfile(payload),
    enabled: !!payload.profileId,
    ...options,
  });
};

export const useGetCalendarManagement = (
  payload: GetCalendarManagementPayload,
  options?: Omit<
    UseQueryOptions<GetCalendarManagementResponse, Error>,
    'queryKey'
  >,
): UseQueryResult<GetCalendarManagementResponse, Error> => {
  return useQuery<GetCalendarManagementResponse, Error>({
    queryKey: ['getCalendarManagement', payload],
    queryFn: () => getCalendarManagement(payload),
    enabled: !!payload.id,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    ...options,
  });
};

export const useReserveBooking = (): UseMutationResult<
  unknown,
  Error,
  BookingReservePayload
> => {
  return useMutation({
    mutationFn: reserveBooking,
  });
};

export const useFilterDoctorsBySpecialityAndPlace = (
  payload: FilterDoctorsBySpecialityAndPlacePayload,
  options?: Omit<
    UseQueryOptions<FilterDoctorsBySpecialityAndPlaceResponse, Error>,
    'queryKey'
  >,
): UseQueryResult<FilterDoctorsBySpecialityAndPlaceResponse, Error> => {
  return useQuery<FilterDoctorsBySpecialityAndPlaceResponse, Error>({
    queryKey: [
      'filterDoctorsBySpecialityAndPlace',
      payload.LatestSpecialityId,
      payload.ProfileId,
    ],
    queryFn: () => filterDoctorsBySpecialityAndPlace(payload),
    enabled: !!payload.LatestSpecialityId && !!payload.ProfileId,
    ...options,
  });
};

export const useSearchClinic = (
  payload: SearchClinicPayload,
  options?: Omit<UseQueryOptions<SearchClinicResponse, Error>, 'queryKey'>,
): UseQueryResult<SearchClinicResponse, Error> => {
  return useQuery<SearchClinicResponse, Error>({
    queryKey: ['searchClinic', payload],
    queryFn: () => searchClinic(payload),
    enabled: !!payload.search,
    ...options,
  });
};
